import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Reusable video component with language-aware subtitles
 * Optimized for cross-browser compatibility and mobile playback
 */
const LanguageAwareVideo = ({
  videoUrl,
  subtitles = [], // Array of { src, lang, label }
  className = '',
  containerClassName = '',
  showLoadingState = true,
  testId = 'language-aware-video',
  aspectRatio = 'aspect-video'
}) => {
  const { language } = useLanguage();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const observerRef = useRef(null);

  // Update subtitle tracks based on language
  const updateSubtitleTracks = useCallback(() => {
    const video = videoRef.current;
    if (!video?.textTracks?.length) return;

    const tracks = video.textTracks;
    
    // Disable all tracks first
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = 'disabled';
    }

    // Enable the track that matches the current site language
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].language === language) {
        tracks[i].mode = 'showing';
        break;
      }
    }
  }, [language]);

  // Lazy load with IntersectionObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container || shouldLoad) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observerRef.current?.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observerRef.current.observe(container);

    return () => observerRef.current?.disconnect();
  }, [shouldLoad]);

  // Handle language changes
  useEffect(() => {
    updateSubtitleTracks();
  }, [updateSubtitleTracks]);

  // Video ready detection with fallback timer
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;

    const markReady = () => {
      if (!isReady) {
        setIsReady(true);
        updateSubtitleTracks();
      }
    };

    // Check if already loaded
    if (video.readyState >= 1) {
      markReady();
      return;
    }

    // Set up event listeners
    video.addEventListener('loadedmetadata', markReady);
    video.addEventListener('canplay', markReady);

    // Fallback timer for browsers that don't fire events properly
    const fallbackTimer = setTimeout(markReady, 3000);

    return () => {
      video.removeEventListener('loadedmetadata', markReady);
      video.removeEventListener('canplay', markReady);
      clearTimeout(fallbackTimer);
    };
  }, [shouldLoad, isReady, updateSubtitleTracks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={`relative ${aspectRatio} ${containerClassName}`}>
      {/* Loading indicator */}
      {showLoadingState && !isReady && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-900 pointer-events-none">
          <div className="animate-pulse">
            <Play className="w-12 h-12 sm:w-16 sm:h-16 text-slate-400" />
          </div>
        </div>
      )}
      
      {/* Video element */}
      <video
        ref={videoRef}
        controls
        playsInline
        preload={shouldLoad ? 'metadata' : 'none'}
        className={`w-full h-full object-contain transition-opacity duration-300 ${
          isReady ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        data-testid={testId}
      >
        {shouldLoad && <source src={videoUrl} type="video/mp4" />}
        {shouldLoad && subtitles.map((sub) => (
          <track
            key={`${sub.lang}-${sub.src}`}
            kind="subtitles"
            src={sub.src}
            srcLang={sub.lang}
            label={sub.label}
          />
        ))}
      </video>
    </div>
  );
};

export default LanguageAwareVideo;
